import React, { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { HelpCircle, Search, ArrowLeft, Mail } from "lucide-react";

const FAQS = [
  {
    q: "Will my clothes be mixed with other customers’ laundry?",
    a: "No. Every customer’s laundry is washed separately — your load never touches anyone else’s.",
  },
  {
    q: "What happens if an item is damaged?",
    a: "We handle every order with care. If an issue occurs, please contact us within 24 hours of delivery so we can review your concern and make it right.",
  },
  {
    q: "Can I include special instructions?",
    a: "Yes. Add notes at checkout for preferred detergent, water temperature, folding preferences, or stain concerns — we follow them exactly.",
  },
  {
    q: "Do I need to separate my clothes?",
    a: "No. We sort your laundry by colour and fabric before washing, so you don’t have to lift a finger.",
  },
  {
    q: "How should I prepare my laundry?",
    a: "Place your laundry in a bag, remove personal items from pockets, and let us know if you have any special washing instructions.",
  },
  {
    q: "Do you offer recurring weekly service?",
    a: "Yes! You can schedule weekly or bi-weekly pickups for added convenience — check our Membership plans for even more savings.",
  },
  {
    q: "How long does it take to get my laundry back?",
    a: "Most orders are returned within 24 hours. Standard drop-off is on the next Tue / Thu / Sat window; add Express for same-day return.",
  },
  {
    q: "Do I need to be home for pickup and delivery?",
    a: "Not always. You can leave instructions for a safe drop-off or pickup location — buzzer codes, doorman details, or a preferred spot at the door.",
  },
  {
    q: "What items do you NOT accept?",
    a: (
      <ul className="list-disc pl-6 space-y-1">
        <li>Wet or mouldy items</li>
        <li>Heavily soiled items with hazardous materials</li>
        <li>Items contaminated with chemicals, gasoline, or bodily fluids</li>
      </ul>
    ),
  },
];

export default function FAQ() {
  const [q, setQ] = useState("");
  const filtered = FAQS.filter((f) =>
    !q ||
    f.q.toLowerCase().includes(q.toLowerCase()) ||
    (typeof f.a === "string" && f.a.toLowerCase().includes(q.toLowerCase()))
  );

  return (
    <div data-testid="faq-page" className="min-h-screen bg-stone-50">
      <Navbar />
      <div className="max-w-3xl mx-auto px-6 py-12">
        <Link to="/" className="inline-flex items-center gap-1 text-stone-500 hover:text-pink-600 text-sm font-semibold mb-6">
          <ArrowLeft className="w-4 h-4" /> Back to home
        </Link>
        <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-pink-500 mb-3">
          <HelpCircle className="w-3.5 h-3.5" /> FAQ
        </div>
        <h1 className="font-heading text-4xl lg:text-5xl font-black tracking-tighter text-stone-900">
          Everything you need to know.
        </h1>
        <p className="text-stone-600 mt-3">
          Quick answers to the questions we hear most. Can&rsquo;t find what you&rsquo;re after? Just email us.
        </p>

        <div className="relative mt-8">
          <Search className="w-4 h-4 text-stone-400 absolute left-4 top-1/2 -translate-y-1/2" />
          <Input
            data-testid="faq-search"
            placeholder="Search questions…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-11 h-12 rounded-full border-stone-300"
          />
        </div>

        <Card className="mt-8 p-2 sm:p-4 rounded-3xl border border-stone-200 bg-white">
          {filtered.length === 0 ? (
            <div className="p-10 text-center text-stone-500">
              Nothing matches &ldquo;{q}&rdquo;. Try a different search or email us below.
            </div>
          ) : (
            <Accordion type="single" collapsible className="w-full">
              {filtered.map((f, i) => (
                <AccordionItem
                  key={f.q}
                  value={`item-${i}`}
                  data-testid={`faq-item-${i}`}
                  className="border-b border-stone-100 last:border-0"
                >
                  <AccordionTrigger className="text-left font-heading font-bold text-stone-900 text-lg hover:no-underline px-4">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-stone-700 leading-relaxed px-4 pb-5">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          )}
        </Card>

        <Card className="mt-8 p-6 rounded-3xl border-2 border-pink-200 bg-gradient-to-br from-pink-50 to-white flex flex-col sm:flex-row items-center justify-between gap-4">
          <div>
            <div className="font-heading font-black text-xl text-stone-900">Still have questions?</div>
            <p className="text-sm text-stone-600 mt-1">We reply within one business day.</p>
          </div>
          <a
            href="mailto:info@foldora.ca?subject=Foldora%20FAQ%20question"
            data-testid="faq-email-cta"
            className="inline-flex items-center gap-2 rounded-full bg-pink-500 hover:bg-pink-600 text-white font-bold h-12 px-6"
          >
            <Mail className="w-4 h-4" /> Email us
          </a>
        </Card>
      </div>
    </div>
  );
}
